<template>
  <div class="booking-list-container">
    <h2>预订列表</h2>
    <el-card shadow="hover">
      <div class="card-header">
        <el-input
          v-model="searchQuery"
          placeholder="搜索预订号或客户名称"
          style="width: 300px; margin-right: 10px;"
          clearable
          @input="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
        <el-button type="primary" @click="handleCreate">
          <el-icon><Plus /></el-icon> 添加预订
        </el-button>
      </div>
      
      <el-table :data="bookings" border stripe style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="id" label="预订号" width="150">
          <template #default="scope">
            YD{{ String(scope.row.id).padStart(6, '0') }}
          </template>
        </el-table-column>
        <el-table-column prop="room.room_number" label="房间号" />
        <el-table-column prop="customer_name" label="客户姓名" />
        <el-table-column prop="customer_phone" label="客户电话" />
        <el-table-column prop="check_in_date" label="入住日期" width="150" />
        <el-table-column prop="check_out_date" label="退房日期" width="150" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="getStatusType(scope.row.status)">
              {{ getStatusName(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="payment_status" label="支付状态" width="100">
          <template #default="scope">
            <el-tag :type="getPaymentStatusType(scope.row.payment_status)">
              {{ getPaymentStatusName(scope.row.payment_status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="total_amount" label="总金额" />
        <el-table-column label="操作" width="250" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleViewDetails(scope.row)">
              <el-icon><View /></el-icon> 查看详情
            </el-button>
            <el-button type="success" size="small" @click="handleEdit(scope.row)">
              <el-icon><Edit /></el-icon> 编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row)">
              <el-icon><Delete /></el-icon> 删除
            </el-button>
            <el-dropdown trigger="click">
              <el-button size="small">
                操作 <el-icon class="el-icon--right"><ArrowDown /></el-icon>
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item @click="handleConfirm(scope.row)" :disabled="scope.row.status !== 'pending'">确认预订</el-dropdown-item>
                  <el-dropdown-item @click="handleCancelBooking(scope.row)" :disabled="['cancelled', 'completed'].includes(scope.row.status)">取消预订</el-dropdown-item>
                  <el-dropdown-item @click="handleComplete(scope.row)" :disabled="['cancelled', 'completed'].includes(scope.row.status)">完成预订</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, View, Edit, Delete, ArrowDown } from '@element-plus/icons-vue';
import service from '../../api/axios';

export default {
  name: 'BookingList',
  components: {
    Search,
    Plus,
    View,
    Edit,
    Delete,
    ArrowDown
  },
  setup() {
    const router = useRouter();
    
    const bookings = ref([]);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);
    
    // 获取预订列表
    const fetchBookings = async () => {
      loading.value = true;
      try {
        const response = await service({
          url: '/manage/bookings',
          method: 'get',
          params: {
            page: currentPage.value,
            pageSize: pageSize.value,
            search: searchQuery.value
          }
        });
        // 确保bookings始终是数组，避免Element Plus表格出错
        bookings.value = Array.isArray(response.data.data) ? response.data.data : [];
        total.value = response.data.total || 0;
      } catch (error) {
        ElMessage.error('获取预订列表失败');
        console.error(error);
        // 出错时确保bookings是数组
        bookings.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };
    
    // 页面加载时获取预订列表
    onMounted(() => {
      fetchBookings();
    });
    
    // 搜索预订
    const handleSearch = () => {
      fetchBookings();
    };
    
    // 跳转到添加预订页面
    const handleCreate = () => {
      router.push('/bookings/create');
    };
    
    // 查看预订详情
    const handleViewDetails = (booking) => {
      router.push({
        path: `/bookings/detail/${booking.id}`
      });
    };
    
    // 跳转到编辑预订页面
    const handleEdit = (booking) => {
      router.push({
        path: `/bookings/edit/${booking.id}`
      });
    };
    
    // 删除预订
    const handleDelete = (booking) => {
      ElMessageBox.confirm('确定要删除该预订吗？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await service({
            url: `/manage/bookings/${booking.id}`,
            method: 'delete'
          });
          ElMessage.success('删除成功');
          fetchBookings();
        } catch (error) {
          ElMessage.error('删除失败');
          console.error(error);
        }
      }).catch(() => {
        // 取消删除操作
      });
    };
    
    // 确认预订
    const handleConfirm = async (booking) => {
      try {
        await service({
          url: `/manage/bookings/${booking.id}/confirm`,
          method: 'post'
        });
        ElMessage.success('预订已确认');
        fetchBookings();
      } catch (error) {
        ElMessage.error('确认预订失败');
        console.error(error);
      }
    };
    
    // 取消预订
    const handleCancelBooking = async (booking) => {
      try {
        await service({
          url: `/manage/bookings/${booking.id}/cancel`,
          method: 'post'
        });
        ElMessage.success('预订已取消');
        fetchBookings();
      } catch (error) {
        ElMessage.error('取消预订失败');
        console.error(error);
      }
    };
    
    // 完成预订
    const handleComplete = async (booking) => {
      try {
        await service({
          url: `/manage/bookings/${booking.id}/complete`,
          method: 'post'
        });
        ElMessage.success('预订已完成');
        fetchBookings();
      } catch (error) {
        ElMessage.error('完成预订失败');
        console.error(error);
      }
    };
    
    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pageSize.value = size;
      fetchBookings();
    };
    
    // 处理当前页变化
    const handleCurrentChange = (current) => {
      currentPage.value = current;
      fetchBookings();
    };
    
    // 获取状态类型
    const getStatusType = (status) => {
      const statusMap = {
        'pending': 'warning',
        'confirmed': 'success',
        'completed': 'success',
        'cancelled': 'danger'
      };
      return statusMap[status] || 'info';
    };
    
    // 获取状态名称
    const getStatusName = (status) => {
      const statusMap = {
        'pending': '待确认',
        'confirmed': '已确认',
        'completed': '已完成',
        'cancelled': '已取消'
      };
      return statusMap[status] || status;
    };
    
    // 获取支付状态类型
    const getPaymentStatusType = (status) => {
      const statusMap = {
        'unpaid': 'warning',
        'paid': 'success',
        'partially_paid': 'info'
      };
      return statusMap[status] || 'info';
    };
    
    // 获取支付状态名称
    const getPaymentStatusName = (status) => {
      const statusMap = {
        'unpaid': '未支付',
        'paid': '已支付',
        'partially_paid': '部分支付'
      };
      return statusMap[status] || status;
    };
    
    return {
      bookings,
      searchQuery,
      currentPage,
      pageSize,
      total,
      loading,
      handleCreate,
      handleViewDetails,
      handleEdit,
      handleDelete,
      handleConfirm,
      handleCancelBooking,
      handleComplete,
      handleSizeChange,
      handleCurrentChange,
      handleSearch,
      getStatusType,
      getStatusName,
      getPaymentStatusType,
      getPaymentStatusName
    };
  }
};
</script>

<style scoped>
.booking-list-container {
  padding: 20px;
}

.booking-list-container h2 {
  margin-bottom: 20px;
  color: #333;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>